import React from 'react'
import { cn } from 'oddle-shop-monorepo.design-system'

interface ColumnProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode
  className?: string
  // Grid sizes (1-12)
  sm?: number
  md?: number
  lg?: number
  xl?: number
  // Order
  order?: number
  // Offsets
  offsetSm?: number
  offsetMd?: number
  offsetLg?: number
  offsetXl?: number
  // Hidden states
  smHidden?: boolean
  mdHidden?: boolean
  lgHidden?: boolean
  xlHidden?: boolean
  // Custom gutter size
  gutterSize?: number
}

const Column: React.FC<ColumnProps> = ({
  children,
  className,
  sm,
  md,
  lg,
  xl,
  order,
  offsetSm,
  offsetMd,
  offsetLg,
  offsetXl,
  smHidden,
  mdHidden,
  lgHidden,
  xlHidden,
  gutterSize,
  ...props
}) => {
  // Base classes
  const baseClasses = [
    'relative',
    'w-full',
    'max-w-full',
    'min-h-[1px]',
    'flex-grow',
    'px-2', // Default mobile gutter (8px each side = 16px total)
  ]

  // Responsive classes
  const responsiveClasses = []

  // Type for valid column sizes (1-12)
  type ColumnSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
  type OffsetSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11

  // Predefined grid class mappings for each breakpoint
  const gridClasses = {
    sm: {
      1: 'sm:w-1/12',
      2: 'sm:w-1/6',
      3: 'sm:w-1/4',
      4: 'sm:w-1/3',
      5: 'sm:w-5/12',
      6: 'sm:w-1/2',
      7: 'sm:w-7/12',
      8: 'sm:w-2/3',
      9: 'sm:w-3/4',
      10: 'sm:w-5/6',
      11: 'sm:w-11/12',
      12: 'sm:w-full',
    },
    md: {
      1: 'md:w-1/12',
      2: 'md:w-1/6',
      3: 'md:w-1/4',
      4: 'md:w-1/3',
      5: 'md:w-5/12',
      6: 'md:w-1/2',
      7: 'md:w-7/12',
      8: 'md:w-2/3',
      9: 'md:w-3/4',
      10: 'md:w-5/6',
      11: 'md:w-11/12',
      12: 'md:w-full',
    },
    lg: {
      1: 'lg:w-1/12',
      2: 'lg:w-1/6',
      3: 'lg:w-1/4',
      4: 'lg:w-1/3',
      5: 'lg:w-5/12',
      6: 'lg:w-1/2',
      7: 'lg:w-7/12',
      8: 'lg:w-2/3',
      9: 'lg:w-3/4',
      10: 'lg:w-5/6',
      11: 'lg:w-11/12',
      12: 'lg:w-full',
    },
    xl: {
      1: 'xl:w-1/12',
      2: 'xl:w-1/6',
      3: 'xl:w-1/4',
      4: 'xl:w-1/3',
      5: 'xl:w-5/12',
      6: 'xl:w-1/2',
      7: 'xl:w-7/12',
      8: 'xl:w-2/3',
      9: 'xl:w-3/4',
      10: 'xl:w-5/6',
      11: 'xl:w-11/12',
      12: 'xl:w-full',
    },
  } as const

  const offsetClasses = {
    sm: {
      1: 'sm:ml-[8.333333%]',
      2: 'sm:ml-[16.666667%]',
      3: 'sm:ml-[25%]',
      4: 'sm:ml-[33.333333%]',
      5: 'sm:ml-[41.666667%]',
      6: 'sm:ml-[50%]',
      7: 'sm:ml-[58.333333%]',
      8: 'sm:ml-[66.666667%]',
      9: 'sm:ml-[75%]',
      10: 'sm:ml-[83.333333%]',
      11: 'sm:ml-[91.666667%]',
    },
    md: {
      1: 'md:ml-[8.333333%]',
      2: 'md:ml-[16.666667%]',
      3: 'md:ml-[25%]',
      4: 'md:ml-[33.333333%]',
      5: 'md:ml-[41.666667%]',
      6: 'md:ml-[50%]',
      7: 'md:ml-[58.333333%]',
      8: 'md:ml-[66.666667%]',
      9: 'md:ml-[75%]',
      10: 'md:ml-[83.333333%]',
      11: 'md:ml-[91.666667%]',
    },
    lg: {
      1: 'lg:ml-[8.333333%]',
      2: 'lg:ml-[16.666667%]',
      3: 'lg:ml-[25%]',
      4: 'lg:ml-[33.333333%]',
      5: 'lg:ml-[41.666667%]',
      6: 'lg:ml-[50%]',
      7: 'lg:ml-[58.333333%]',
      8: 'lg:ml-[66.666667%]',
      9: 'lg:ml-[75%]',
      10: 'lg:ml-[83.333333%]',
      11: 'lg:ml-[91.666667%]',
    },
    xl: {
      1: 'xl:ml-[8.333333%]',
      2: 'xl:ml-[16.666667%]',
      3: 'xl:ml-[25%]',
      4: 'xl:ml-[33.333333%]',
      5: 'xl:ml-[41.666667%]',
      6: 'xl:ml-[50%]',
      7: 'xl:ml-[58.333333%]',
      8: 'xl:ml-[66.666667%]',
      9: 'xl:ml-[75%]',
      10: 'xl:ml-[83.333333%]',
      11: 'xl:ml-[91.666667%]',
    },
  } as const

  // Small (mobile) breakpoint
  if (sm && sm >= 1 && sm <= 12) {
    responsiveClasses.push(gridClasses.sm[sm as ColumnSize])
  }
  if (offsetSm && offsetSm >= 1 && offsetSm <= 11) {
    responsiveClasses.push(offsetClasses.sm[offsetSm as OffsetSize])
  }
  if (smHidden) {
    responsiveClasses.push('sm:hidden')
  }

  // Medium (tablet) breakpoint - update padding
  responsiveClasses.push('md:block', 'md:px-2') // 16px gutter on tablet

  if (md && md >= 1 && md <= 12) {
    responsiveClasses.push(gridClasses.md[md as ColumnSize])
  }
  if (offsetMd && offsetMd >= 1 && offsetMd <= 11) {
    responsiveClasses.push(offsetClasses.md[offsetMd as OffsetSize])
  }
  if (mdHidden) {
    responsiveClasses.push('md:hidden')
  }

  // Large (desktop) breakpoint - update padding
  responsiveClasses.push('lg:block', 'lg:px-4') // 32px gutter on desktop

  if (lg && lg >= 1 && lg <= 12) {
    responsiveClasses.push(gridClasses.lg[lg as ColumnSize])
  }
  if (offsetLg && offsetLg >= 1 && offsetLg <= 11) {
    responsiveClasses.push(offsetClasses.lg[offsetLg as OffsetSize])
  }
  if (lgHidden) {
    responsiveClasses.push('lg:hidden')
  }

  // Extra large breakpoint
  if (xl && xl >= 1 && xl <= 12) {
    responsiveClasses.push(gridClasses.xl[xl as ColumnSize])
  }
  if (offsetXl && offsetXl >= 1 && offsetXl <= 11) {
    responsiveClasses.push(offsetClasses.xl[offsetXl as OffsetSize])
  }
  if (xlHidden) {
    responsiveClasses.push('xl:hidden')
  }

  // Order
  if (order !== undefined) {
    responsiveClasses.push(`order-${order}`)
  }

  // Custom gutter size styles
  const customStyles = gutterSize
    ? {
        paddingLeft: `${gutterSize / 2}px`,
        paddingRight: `${gutterSize / 2}px`,
      }
    : undefined

  return (
    <div
      className={cn(...baseClasses, ...responsiveClasses, className)}
      style={customStyles}
      {...props}
    >
      {children}
    </div>
  )
}

export default Column
